import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Business as ClientsIcon,
  ConfirmationNumber as TicketsIcon,
  Folder as ProjectsIcon,
  Devices as AssetsIcon,
  RequestQuote as QuotesIcon,
  Description as KnowledgeBaseIcon,
  Receipt as InvoicesIcon,
  Key as CredentialsIcon,
  Inventory as ProductsIcon,
  Factory as VendorsIcon,
  Payment as PaymentsIcon,
  Subscriptions as SubscriptionsIcon,
  People as UsersIcon,
  Settings as SettingsIcon,
  Backup as BackupIcon,
  Logout as LogoutIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  ChevronLeft as ChevronLeftIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  MonitorHeart as MonitoringIcon,
  SwapHoriz as HandoverIcon,
  VerifiedUser as CyberEssentialsIcon,
  Draw as SignatureIcon,
  Hub as IntegrationsIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from '../contexts/ToastContext';
import api from '../api/apiConfig';
import CommandPalette from './CommandPalette';

const drawerWidth = 200;

const dashboardItem = { text: 'Dashboard', icon: <DashboardIcon fontSize="small" />, path: '/dashboard' };

const menuSections = [
  {
    id: 'crm',
    header: 'CRM',
    items: [
      { text: 'Clients', icon: <ClientsIcon fontSize="small" />, path: '/clients' },
      { text: 'Contacts', icon: <UsersIcon fontSize="small" />, path: '/contacts' },
      { text: 'Handover', icon: <HandoverIcon fontSize="small" />, path: '/handover' }
    ]
  },
  {
    id: 'work',
    header: 'Work',
    items: [
      { text: 'Tickets', icon: <TicketsIcon fontSize="small" />, path: '/tickets' },
      { text: 'Monitoring', icon: <MonitoringIcon fontSize="small" />, path: '/monitoring' },
      { text: 'Devices', icon: <AssetsIcon fontSize="small" />, path: '/rmm' },
      { text: 'Endpoint Backups', icon: <BackupIcon fontSize="small" />, path: '/managed-backups', adminOnly: true },
      { text: 'Cyber Essentials', icon: <CyberEssentialsIcon fontSize="small" />, path: '/cyber-essentials' },
      { text: 'Signatures', icon: <SignatureIcon fontSize="small" />, path: '/signatures' },
      { text: 'Projects', icon: <ProjectsIcon fontSize="small" />, path: '/projects' }
    ]
  },
  {
    id: 'billing',
    header: 'Billing',
    items: [
      { text: 'Quotes', icon: <QuotesIcon fontSize="small" />, path: '/quotes' },
      { text: 'Invoices', icon: <InvoicesIcon fontSize="small" />, path: '/invoices' },
      { text: 'Payments', icon: <PaymentsIcon fontSize="small" />, path: '/payments' }
    ]
  },
  {
    id: 'inventory',
    header: 'Inventory',
    items: [
      { text: 'Assets', icon: <AssetsIcon fontSize="small" />, path: '/assets' },
      { text: 'Renewals', icon: <SubscriptionsIcon fontSize="small" />, path: '/renewals' }
    ]
  },
  {
    id: 'resources',
    header: 'Resources',
    items: [
      { text: 'Services', icon: <IntegrationsIcon fontSize="small" />, path: '/services' },
      { text: 'Credentials', icon: <CredentialsIcon fontSize="small" />, path: '/credentials' },
      { text: 'Knowledge Base', icon: <KnowledgeBaseIcon fontSize="small" />, path: '/knowledge-base' }
    ]
  },
  {
    id: 'catalogue',
    // Was "Config", which read as a second settings area. Integrations and
    // Microsoft 365 moved into Settings, where they belong; what is left is
    // reference data you maintain, not configuration.
    header: 'Catalogue',
    items: [
      { text: 'Products', icon: <ProductsIcon fontSize="small" />, path: '/products' },
      { text: 'Vendors', icon: <VendorsIcon fontSize="small" />, path: '/vendors' },
      { text: 'Server Types', icon: <SettingsIcon fontSize="small" />, path: '/server-functions' }
    ]
  }
];

const Layout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  // Every section used to start expanded, which put all 25 destinations on
  // screen at once and made the collapse machinery below pointless. Sections
  // now start CLOSED; isSectionExpanded still force-opens whichever one holds
  // the current route, so you always see where you are and nothing is buried.
  // Your own expansions are remembered so a section you actually use stays put.
  const [expandedSections, setExpandedSections] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('navSections') || '{}');
      if (stored && typeof stored === 'object') return stored;
    } catch {
      /* corrupt value - fall through to all closed */
    }
    return {};
  });

  useEffect(() => {
    try {
      localStorage.setItem('navSections', JSON.stringify(expandedSections));
    } catch {
      /* storage full or blocked - the nav still works, it just forgets */
    }
  }, [expandedSections]);
  const [dashboardLogoUrl, setDashboardLogoUrl] = useState('');

  // Open the universal-search command palette with Ctrl+K / Cmd+K from
  // anywhere in the app. We also catch the equivalent on macOS.
  useEffect(() => {
    const handler = (event) => {
      const isCmdK = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
      if (isCmdK) {
        event.preventDefault();
        setPaletteOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);
  const navigate = useNavigate();
  const location = useLocation();
  const { auth, logout } = useAuth();
  const { mode, toggleColorMode, colors } = useTheme();
  const { showSuccess } = useToast();
  const isMobile = useMediaQuery('(max-width:900px)');

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    showSuccess('Logged out successfully');
    navigate('/login');
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const isSelected = (path) => location.pathname === path || location.pathname.startsWith(path + '/');
  const sectionHasActiveRoute = (sectionItems) => sectionItems.some((menuItem) => isSelected(menuItem.path));

  const isSectionExpanded = (section) => expandedSections[section.id] || sectionHasActiveRoute(section.items);

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await api.get('/public/branding');
        // Prefer the light-background variant when the interface is light: a
        // white wordmark on a white page is invisible, and an <img> cannot
        // inherit a colour. Falls back to the single logo when none is set.
        const wantLight = mode !== 'dark';
        const light = response.data?.dashboardLight;
        if (wantLight && light?.configured && light?.url) {
          setDashboardLogoUrl(`${light.url}?v=${Date.now()}`);
        } else if (response.data?.dashboard?.configured && response.data?.dashboard?.url) {
          setDashboardLogoUrl(`${response.data.dashboard.url}?v=${Date.now()}`);
        }
      } catch {
        // Ignore branding fetch errors in layout.
      }
    };
    fetchBranding();
    // Re-run on mode change so toggling light/dark swaps the logo variant.
  }, [mode]);

  const handleToggleSection = (sectionId) => {
    setExpandedSections((prevState) => ({
      ...prevState,
      [sectionId]: !prevState[sectionId]
    }));
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: mode === 'dark' ? colors.black : colors.white }}>
      {/* Logo */}
      <Box
        sx={{
          px: 1.5,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${mode === 'dark' ? colors.borderDark : colors.borderLight}`
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 3,
              height: 24,
              bgcolor: colors.accent,
            }}
          />
          {dashboardLogoUrl ? (
            <Box
              sx={{
                width: 170,
                height: 42,
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0
              }}
            >
              <Box
                component="img"
                src={dashboardLogoUrl}
                alt="Company logo"
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'block',
                  objectFit: 'contain',
                  objectPosition: 'left center'
                }}
              />
            </Box>
          ) : (
            <Typography
              sx={{
                fontFamily: '"JetBrains Mono", monospace',
                fontWeight: 700,
                fontSize: '0.875rem',
                color: mode === 'dark' ? colors.textWhite : colors.textDark,
                textTransform: 'uppercase',
                letterSpacing: '0.1em'
              }}
            >
              Zone
            </Typography>
          )}
        </Box>
        {isMobile && (
          <IconButton onClick={handleDrawerToggle} sx={{ color: mode === 'dark' ? colors.textMuted : colors.textDarkSecondary, p: 0.5 }}>
            <ChevronLeftIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Navigation */}
      <List sx={{ flex: 1, px: 0.5, py: 1 }}>
        <ListItem key={`item-${dashboardItem.text}`} disablePadding sx={{ mb: 0 }}>
          <ListItemButton
            selected={isSelected(dashboardItem.path)}
            onClick={() => handleNavigate(dashboardItem.path)}
          >
            <ListItemIcon>
              {dashboardItem.icon}
            </ListItemIcon>
            <ListItemText primary={dashboardItem.text} />
          </ListItemButton>
        </ListItem>

        {menuSections.map((section) => {
          const expanded = isSectionExpanded(section);
          const items = section.items.filter((it) => !it.adminOnly || auth?.user?.role === 'admin');
          if (!items.length) return null;
          return (
            <Box key={`section-${section.id}`}>
              <ListItem disablePadding sx={{ mt: 0.5 }}>
                <ListItemButton
                  onClick={() => handleToggleSection(section.id)}
                  sx={{
                    minHeight: 28,
                    px: 1.5,
                    py: 0.25,
                    borderRadius: 1
                  }}
                >
                  <Typography
                    sx={{
                      flexGrow: 1,
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: '0.5625rem',
                      fontWeight: 600,
                      color: mode === 'dark' ? colors.textDarkSecondary : colors.textMuted,
                      textTransform: 'uppercase',
                      letterSpacing: '0.15em'
                    }}
                  >
                    {section.header}
                  </Typography>
                  {expanded ? (
                    <ExpandLessIcon sx={{ fontSize: '0.875rem', color: mode === 'dark' ? colors.textDarkSecondary : colors.textMuted }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: '0.875rem', color: mode === 'dark' ? colors.textDarkSecondary : colors.textMuted }} />
                  )}
                </ListItemButton>
              </ListItem>

              <Box
                sx={{
                  maxHeight: expanded ? items.length * 40 : 0,
                  overflow: 'hidden',
                  transition: 'max-height 0.2s ease'
                }}
              >
                {items.map((item) => (
                  <ListItem key={`item-${item.text}`} disablePadding sx={{ mb: 0 }}>
                    <ListItemButton
                      selected={isSelected(item.path)}
                      onClick={() => handleNavigate(item.path)}
                    >
                      <ListItemIcon>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.text} />
                    </ListItemButton>
                  </ListItem>
                ))}
              </Box>
            </Box>
          );
        })}
      </List>

      {/* Admin Section */}
      {auth?.user?.role === 'admin' && (
        <Box sx={{ px: 0.5, pb: 1 }}>
          <Divider sx={{ mb: 0.5, mx: 1, borderColor: mode === 'dark' ? colors.borderDark : colors.borderLight }} />
          <ListItem disablePadding>
            <ListItemButton
              selected={isSelected('/users')}
              onClick={() => handleNavigate('/users')}
            >
              <ListItemIcon>
                <UsersIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Users" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={isSelected('/settings')}
              onClick={() => handleNavigate('/settings')}
            >
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Settings" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              selected={isSelected('/backups')}
              onClick={() => handleNavigate('/backups')}
            >
              <ListItemIcon>
                <BackupIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Backups" />
            </ListItemButton>
          </ListItem>
        </Box>
      )}

      {/* Version */}
      <Box sx={{ px: 1.5, py: 1, borderTop: `1px solid ${mode === 'dark' ? colors.borderDark : colors.borderLight}` }}>
        <Typography
          sx={{
            fontFamily: '"JetBrains Mono", monospace',
            fontSize: '0.5625rem',
            color: mode === 'dark' ? colors.textDarkSecondary : colors.textMuted,
            textTransform: 'uppercase',
            letterSpacing: '0.1em'
          }}
        >
          v1.0.0
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: mode === 'dark' ? colors.black : colors.white,
          borderLeft: `2px solid ${colors.accent}`,
          boxShadow: 'none',
          borderBottom: `1px solid ${mode === 'dark' ? colors.borderDark : colors.borderLight}`
        }}
      >
        <Toolbar sx={{ minHeight: '48px !important', px: 1.5 }}>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1, display: { md: 'none' } }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title="SEARCH (Ctrl+K)">
            <IconButton
              onClick={() => setPaletteOpen(true)}
              sx={{ color: mode === 'dark' ? colors.textMuted : colors.textDarkSecondary, mr: 0.5 }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title={mode === 'dark' ? 'LIGHT MODE' : 'DARK MODE'}>
            <IconButton onClick={toggleColorMode} sx={{ color: mode === 'dark' ? colors.textMuted : colors.textDarkSecondary, mr: 0.5 }}>
              {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            </IconButton>
          </Tooltip>

          <IconButton onClick={handleMenuClick} sx={{ p: 0.5 }}>
            <Avatar
              sx={{
                width: 28,
                height: 28,
                bgcolor: colors.accent,
                color: colors.textWhite,
                fontSize: '0.6875rem',
                fontWeight: 700,
                fontFamily: '"JetBrains Mono", monospace'
              }}
            >
              {auth?.user?.firstName?.[0]}{auth?.user?.lastName?.[0]}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ px: 1.5, py: 1 }}>
              <Typography 
                sx={{ 
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.75rem',
                  fontWeight: 600 
                }}
              >
                {auth?.user?.firstName} {auth?.user?.lastName}
              </Typography>
              <Typography 
                sx={{ 
                  fontFamily: '"JetBrains Mono", monospace',
                  fontSize: '0.625rem',
                  color: 'text.secondary',
                  textTransform: 'lowercase'
                }}
              >
                {auth?.user?.email}
              </Typography>
            </Box>
            <Divider />
            <MenuItem 
              onClick={() => { handleMenuClose(); navigate('/profile'); }}
              sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', textTransform: 'uppercase' }}
            >
              Profile
            </MenuItem>
            <MenuItem 
              onClick={handleLogout}
              sx={{ fontFamily: '"JetBrains Mono", monospace', fontSize: '0.6875rem', textTransform: 'uppercase' }}
            >
              <LogoutIcon fontSize="small" sx={{ mr: 1, fontSize: '0.875rem' }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer - Mobile */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            bgcolor: mode === 'dark' ? colors.black : colors.white,
            borderRight: `1px solid ${mode === 'dark' ? colors.borderDark : colors.borderLight}`
          }
        }}
      >
        {drawer}
      </Drawer>

      {/* Drawer - Desktop */}
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', md: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            bgcolor: mode === 'dark' ? colors.black : colors.white,
            borderRight: `1px solid ${mode === 'dark' ? colors.borderDark : colors.borderLight}`
          }
        }}
        open
      >
        {drawer}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { xs: '100%', md: `calc(100% - ${drawerWidth}px)` },
          ml: { xs: 0, md: `${drawerWidth}px` },
          minHeight: '100vh',
          bgcolor: 'background.default'
        }}
      >
        <Toolbar sx={{ minHeight: '48px !important' }} />
        <Box sx={{ p: 1.5 }}>
          <Outlet />
        </Box>
      </Box>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </Box>
  );
};

export default Layout;
